import React from "react";
import { Helmet } from "react-helmet-async";
import { BRAND, APP_URLS } from "../lib/content";

export default function SEO({
  title,
  description,
  path = "",
  image = "/og/hidroverse-share.jpg",
  type = "website",
  ldJson,
  keywords,
  preloadImages,
}) {
  const fullTitle = title ? `HidroVerse ${title}` : `HidroVerse™ · Universal Water Systems`;
  // Use the live origin so og:url + canonical match the URL that was actually
  // shared. Falls back to APP_URLS.site during SSR / first paint.
  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    APP_URLS.site;
  const url = `${origin}${path}`;
  const absImage = image.startsWith("http") ? image : `${origin}${image}`;
  // Most social crawlers (WhatsApp, FB, LinkedIn, iMessage, Slack, Discord) do
  // not execute JS — so the canonical OG defaults are also baked into
  // public/index.html. These react-helmet tags refresh them client-side for
  // Twitter (which does execute JS) and for inspection tools.
  const isJpg = absImage.toLowerCase().endsWith(".jpg") || absImage.toLowerCase().endsWith(".jpeg");
  const desc = description || "HidroVerse — Africa's benchmark for intelligent, engineered water systems. Purification, pumps, pools, irrigation, wastewater, compliance. Trusted by luxury lodges, estates, mines, hotels and municipalities.";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {/* canonical + og:url are emitted globally per-route by
          src/components/seo/CanonicalUrl.jsx — emitting them again here
          caused two <link rel="canonical"> tags on every page and
          tripped Search Console "Duplicate, Google chose different
          canonical than user". */}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:secure_url" content={absImage} />
      <meta property="og:image:type" content={isJpg ? "image/jpeg" : "image/png"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`HidroVerse — ${title || "Universal Water Systems"}`} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:locale" content="en_ZA" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:image:alt" content={`HidroVerse — ${title || "Universal Water Systems"}`} />

      {ldJson && (
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      )}

      {/* LCP preload — hero image fetched in parallel with HTML parse.
          Pass `preloadImages={[{ href, type, media }]}` from the page. */}
      {(preloadImages || []).map((p, i) => (
        <link
          key={`${p.href}-${i}`}
          rel="preload"
          as="image"
          href={p.href}
          type={p.type}
          media={p.media}
          fetchPriority="high"
        />
      ))}
    </Helmet>
  );
}

// JSON-LD helpers — Organization vs LocalBusiness vs FAQ vs Article.
// The Home page emits a @graph with both Organization (the brand entity)
// AND LocalBusiness (the Nelspruit-based physical operation) — that's the
// schema.org-recommended pattern for a single-location business.
export const orgLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${APP_URLS.site}/#organization`,
  name: BRAND.name,
  url: APP_URLS.site,
  logo: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png`,
  description: "Universal water systems engineering — purification, pumps, wastewater, monitoring.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: BRAND.phone,
    email: BRAND.email,
    contactType: "customer support",
    areaServed: "ZA",
    availableLanguage: ["en", "af"],
  },
  sameAs: [
    BRAND.social?.linkedin,
    BRAND.social?.instagram,
    BRAND.social?.facebook,
  ].filter(Boolean),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const articleLd = (g) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: g.title,
  description: g.dek,
  author: { "@type": "Organization", name: BRAND.name },
  publisher: { "@type": "Organization", name: BRAND.name, logo: { "@type": "ImageObject", url: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png` } },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${APP_URLS.site}/guides/${g.slug}` },
});

export const productLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.name,
  description: p.short,
  sku: p.slug,
  brand: { "@type": "Brand", name: BRAND.name },
  offers: { "@type": "Offer", priceCurrency: "ZAR", price: p.price, availability: "https://schema.org/InStock" },
});

export const serviceLd = (name, desc) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description: desc,
  provider: { "@type": "Organization", name: BRAND.name },
  areaServed: { "@type": "Country", name: "South Africa" },
});

// LocalBusiness — fires for "lodge water purification Mpumalanga", "Nelspruit water
// engineer", "Lowveld SANS 241 testing" style local searches.
export const localBusinessLd = () => {
  const n = BRAND.nap || {};
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${APP_URLS.site}/#localbusiness`,
    name: BRAND.name,
    description:
      "Engineered water systems — purification, pumps, wastewater, telemetry — for luxury lodges, estates, mines, hotels and municipalities across southern Africa.",
    url: APP_URLS.site,
    image: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png`,
    logo: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png`,
    telephone: BRAND.phone,
    email: BRAND.email,
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: n.street,
      addressLocality: n.locality,
      addressRegion: n.region,
      postalCode: n.postal,
      addressCountry: n.countryCode,
    },
    geo: { "@type": "GeoCoordinates", latitude: n.lat, longitude: n.lng },
    openingHoursSpecification: (n.openingHoursSpec || []).map((o) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: o.day, opens: o.opens, closes: o.closes,
    })),
    areaServed: (n.areaServed || []).map((a) => ({ "@type": "AdministrativeArea", name: a })),
    sameAs: [
      BRAND.social?.linkedin,
      BRAND.social?.instagram,
      BRAND.social?.facebook,
    ].filter(Boolean),
  };
};

// VideoObject — emitted for every embedded YouTube short on /insights so
// Google Search Console can discover them as videos (not just page URLs).
// Wrapped in an ItemList so a single JSON-LD block covers the whole feed.
//
// Per Google: required fields are name, description, thumbnailUrl, uploadDate.
// embedUrl is required for indexing the player; contentUrl points back at the
// canonical YouTube watch page so the rich result links to YouTube directly.
const isoDurationFromMSS = (mss) => {
  if (!mss || typeof mss !== "string") return undefined;
  const [m, s] = mss.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(m)) return undefined;
  const sec = Number.isNaN(s) ? 0 : s;
  return `PT${m}M${sec}S`;
};

export const videoObjectLd = (v) => {
  if (!v.youtube_id) return null;
  const thumb = `https://i.ytimg.com/vi/${v.youtube_id}/maxresdefault.jpg`;
  // GSC requires uploadDate in full ISO 8601 with timezone (schema.org spec).
  // Source data is "YYYY-MM-DD" — expand to noon SAST (UTC+02:00) so it's a
  // valid, unambiguous datetime. Noon avoids any DST edge case.
  const uploadDate = /^\d{4}-\d{2}-\d{2}$/.test(v.date || "")
    ? `${v.date}T12:00:00+02:00`
    : v.date;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.title,
    description: v.caption,
    thumbnailUrl: [
      thumb,
      `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`,
    ],
    uploadDate,
    contentUrl: `https://www.youtube.com/watch?v=${v.youtube_id}`,
    embedUrl: `https://www.youtube.com/embed/${v.youtube_id}`,
    ...(v.duration ? { duration: isoDurationFromMSS(v.duration) } : {}),
    publisher: {
      "@type": "Organization",
      name: BRAND.name,
      logo: {
        "@type": "ImageObject",
        url: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png`,
      },
    },
  };
};

export const videoFeedLd = (videos) => {
  const items = videos
    .map((v) => videoObjectLd(v))
    .filter(Boolean);
  if (!items.length) return null;
  // @graph keeps each VideoObject as a separate, individually-indexable entity
  // — Google reads them as distinct videos rather than a single ItemList wrapper.
  return {
    "@context": "https://schema.org",
    "@graph": items,
  };
};

// BreadcrumbList — every deep page renders this alongside its own page schema.
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: path.startsWith("http") ? path : `${APP_URLS.site}${path}`,
  })),
});
